import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { toast } from "sonner";
import { Link2, Instagram, Twitter, Smartphone, Youtube, Loader2, Check } from "lucide-react";

const PLATFORMS = [
  { id: "instagram" as const, label: "Instagram", icon: Instagram, color: "text-pink-400 border-pink-400/30 hover:bg-pink-400/10" },
  { id: "twitter" as const, label: "Twitter / X", icon: Twitter, color: "text-sky-400 border-sky-400/30 hover:bg-sky-400/10" },
  { id: "tiktok" as const, label: "TikTok", icon: Smartphone, color: "text-purple-400 border-purple-400/30 hover:bg-purple-400/10" },
  { id: "youtube" as const, label: "YouTube", icon: Youtube, color: "text-red-400 border-red-400/30 hover:bg-red-400/10" },
];

interface LinkSocialPostFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submissionId: number;
  existingPostUrl?: string | null;
  existingPlatform?: string | null;
}

export default function LinkSocialPostForm({
  open,
  onOpenChange,
  submissionId,
  existingPostUrl,
  existingPlatform,
}: LinkSocialPostFormProps) {
  const [platform, setPlatform] = useState<"instagram" | "twitter" | "tiktok" | "youtube">(
    (existingPlatform as any) || "instagram"
  );
  const [postUrl, setPostUrl] = useState(existingPostUrl || "");

  const linkMutation = trpc.game.linkSocialPost.useMutation();
  const utils = trpc.useUtils();

  const handleSubmit = async () => {
    if (!postUrl.trim()) {
      toast.error("Please enter the URL of your social media post");
      return;
    }

    try {
      new URL(postUrl); // Validate URL
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    try {
      await linkMutation.mutateAsync({
        submissionId,
        postUrl: postUrl.trim(),
        platform,
      });
      toast.success("Social post linked! Engagement will be tracked for hybrid scoring.");
      utils.game.socialProofDashboard.invalidate();
      utils.game.getSubmissions.invalidate();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to link social post");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background border-border/50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-neon" />
            Link Social Media Post
          </DialogTitle>
          <DialogDescription>
            Connect your social media post to this submission. Engagement (likes, comments, shares) on your post will count toward your score in hybrid voting mode.
          </DialogDescription>
        </DialogHeader>

        {/* Platform Selection */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          {PLATFORMS.map(({ id, label, icon: Icon, color }) => (
            <button
              key={id}
              onClick={() => setPlatform(id)}
              className={`flex items-center gap-2 p-3 rounded-lg border transition-all text-left text-sm ${
                platform === id
                  ? `${color} bg-opacity-10`
                  : "border-border/50 text-muted-foreground hover:border-border"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </button>
          ))}
        </div>

        {/* URL Input */}
        <div className="space-y-2 mt-2">
          <label className="text-sm font-medium">Post URL</label>
          <Input
            value={postUrl}
            onChange={(e) => setPostUrl(e.target.value)}
            placeholder={`https://${platform === "twitter" ? "x.com" : platform + ".com"}/...`}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Paste the direct link to your post about this submission.
          </p>
        </div>

        {existingPostUrl && (
          <Badge variant="outline" className="text-xs gap-1">
            <Check className="w-3 h-3 text-green-400" />
            Currently linked: {existingPlatform}
          </Badge>
        )}

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={linkMutation.isPending || !postUrl.trim()}
          className="w-full gap-2 bg-neon hover:bg-neon/90 text-background mt-2"
        >
          {linkMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Link2 className="w-4 h-4" />
          )}
          {existingPostUrl ? "Update Linked Post" : "Link Post"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
